import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Lock, Plus, ShieldCheck, Trash2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ModeToggle, PhoneField } from "@/components/PhoneField";
import { ADMIN_NAV } from "@/pages/admin/nav";
import { adminService } from "@/services/adminService";
import { brandService } from "@/services/brandService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#94A3B8]";
const ROLES = [
  { key: "manager", label: "Gestor" },
  { key: "agent", label: "Agente Plus" },
  { key: "supervisor", label: "Supervisor" },
  { key: "admin", label: "Administrador" },
];

export default function AdminTeamPage() {
  const [team, setTeam] = useState([]);
  const [brands, setBrands] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [mode, setMode] = useState("email");
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "manager", phone_dial: "+351", phone: "",
  });

  const load = useCallback(async () => {
    try {
      setTeam(await adminService.listUsers({ roles: "manager,agent,admin,supervisor" }));
      setBrands(await brandService.list());
    } catch (e) { toast.error(errorMessage(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminService.createUser({
        name: form.name,
        password: form.password,
        role: form.role,
        virtual_balance: 0,
        ...(mode === "phone" ? { phone_dial: form.phone_dial, phone: form.phone } : { email: form.email }),
      });
      toast.success("Membro da equipa criado");
      setCreateOpen(false);
      setForm({ name: "", email: "", password: "", role: "manager", phone_dial: "+351", phone: "" });
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const changeRole = async (member, role) => {
    try {
      await adminService.updateUser(member.id, { role });
      toast.success("Perfil atualizado");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const changeBrand = async (member, brand_id) => {
    try {
      await adminService.updateUser(member.id, { brand_id: brand_id || "" });
      toast.success("Brand atualizada");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const resetPassword = async (member) => {
    const password = window.prompt(`Nova palavra-passe para ${member.name} (mínimo 6 caracteres)`);
    if (!password) return;
    if (password.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    try {
      await adminService.updateUser(member.id, { password });
      toast.success("Palavra-passe alterada");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const toggleStatus = async (member) => {
    const status = member.status === "active" ? "suspended" : "active";
    try {
      await adminService.updateUser(member.id, { status });
      toast.success(status === "active" ? "Acesso reativado" : "Acesso bloqueado");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const remove = async (member) => {
    if (!window.confirm(`Remover ${member.name} da equipa?`)) return;
    try {
      await adminService.deleteUser(member.id);
      toast.success("Membro removido");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <AppShell nav={ADMIN_NAV} title="Equipa" subtitle="Gestores e agentes da plataforma">
      <div data-testid="admin-team-page" className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <ShieldCheck className="h-4 w-4 text-[#60A5FA]" /> {team.length} membro(s) interno(s)
          </p>
          <Button data-testid="create-team-member-button" onClick={() => setCreateOpen(true)}
                  className="h-11 rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
            <Plus className="mr-2 h-4 w-4" /> Novo gestor / agente
          </Button>
        </div>

        <div className="overflow-x-auto rounded-md border border-[#22252F] bg-[#12141A]">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="px-6 py-4">Nome</th>
                <th className="px-6 py-4">Contacto</th>
                <th className="px-6 py-4">Perfil</th>
                <th className="px-6 py-4">Brand</th>
                <th className="px-6 py-4">Estado</th>
                <th className="px-6 py-4">Criado</th>
                <th className="px-6 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="team-table-body">
              {team.map((m) => (
                <tr key={m.id} data-testid={`team-row-${m.email || m.phone}`} className="border-b border-[#1A1D25] hover:bg-[#151821]">
                  <td className="px-6 py-4">
                    <Link to={`/team/${m.id}`} data-testid={`team-profile-link-${m.id}`}
                          className="text-white transition-colors hover:text-[#60A5FA]">{m.name}</Link>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-[#94A3B8]">
                    {m.email || `${m.phone_dial || ""} ${m.phone || ""}`}
                  </td>
                  <td className="px-6 py-4">
                    <select
                      data-testid={`team-role-${m.id}`}
                      value={m.role}
                      onChange={(e) => changeRole(m, e.target.value)}
                      className="h-9 rounded-md border border-[#22252F] bg-[#0F1116] px-2 text-xs text-white"
                    >
                      {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      data-testid={`team-brand-${m.id}`}
                      value={m.brand_id || ""}
                      onChange={(e) => changeBrand(m, e.target.value)}
                      className="h-9 rounded-md border border-[#22252F] bg-[#0F1116] px-2 text-xs text-white"
                    >
                      <option value="">Meridian (sem Brand)</option>
                      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-[#94A3B8]">{fmtDate(m.created_at)}</td>
                  <td className="px-6 py-4">
                    <span className={`font-mono text-xs uppercase ${m.status === "active" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                      {m.status === "active" ? "ativo" : "bloqueado"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                    <button data-testid={`reset-password-${m.id}`} onClick={() => resetPassword(m)} title="Alterar palavra-passe"
                            className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:text-white">
                      <KeyRound className="h-4 w-4" />
                    </button>
                    <button data-testid={`toggle-status-${m.id}`} onClick={() => toggleStatus(m)}
                            title={m.status === "active" ? "Bloquear acesso" : "Reativar acesso"}
                            className="rounded-md border border-[#22252F] p-2 text-[#FBBF24] transition-colors hover:border-[#FBBF24]/50">
                      {m.status === "active" ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                    </button>
                    <button data-testid={`delete-team-${m.id}`} onClick={() => remove(m)}
                            className="rounded-md border border-[#22252F] p-2 text-[#EF4444] transition-colors hover:border-[#EF4444]/50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
              {team.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-[#64748B]">Nenhum membro interno</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white sm:max-w-md">
          <DialogHeader><DialogTitle>Novo gestor / agente</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4" data-testid="create-team-form">
            <div className="space-y-2"><Label className={labelCls}>Nome</Label>
              <Input required minLength={2} data-testid="team-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} /></div>
            <ModeToggle mode={mode} onChange={setMode} testIdPrefix="team" />
            {mode === "phone" ? (
              <div className="space-y-2"><Label className={labelCls}>Telefone</Label>
                <PhoneField required dial={form.phone_dial} phone={form.phone}
                            onDial={(v) => setForm({ ...form, phone_dial: v })}
                            onPhone={(v) => setForm({ ...form, phone: v })}
                            testIdPrefix="team-phone" /></div>
            ) : (
              <div className="space-y-2"><Label className={labelCls}>Email</Label>
                <Input required type="email" data-testid="team-email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} /></div>
            )}
            <div className="space-y-2"><Label className={labelCls}>Palavra-passe</Label>
              <Input required type="text" minLength={6} data-testid="team-password" value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} /></div>
            <div className="space-y-2"><Label className={labelCls}>Perfil</Label>
              <select data-testid="team-role-select" value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select></div>
            <Button type="submit" data-testid="submit-team-member"
                    className="w-full rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Criar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
